<template>
    <div>
        <b-form @submit.prevent="onSubmit" @reset.prevent="onReset" novalidate>
            <b-row>
                <b-col md="6" xl="6" sm="6">
                    <label for="institution">{{
                        $t('miniCurriculum.form.label.institution')
                    }}</label>
                    <b-form-input
                        class="mb-2"
                        v-model="form.institution"
                        ref="institution"
                        :state="chkState('institution')"
                        :value.sync="$v.form.institution.$model"
                    />
                    <b-form-invalid-feedback>
                        <span v-if="!$v.form.institution.required">
                            {{ $t('form.validations.required') }}
                        </span>
                        <span v-if="!$v.form.institution.maxLength">
                            {{
                                $t('form.validations.maxLength', {
                                    value: '255',
                                })
                            }}
                        </span>
                    </b-form-invalid-feedback>
                </b-col>

                <b-col md="6" xl="6" sm="6">
                    <label for="course">{{
                        $t('miniCurriculum.form.label.course')
                    }}</label>
                    <b-form-input
                        class="mb-2"
                        ref="course"
                        v-model="form.course"
                        :state="chkState('course')"
                        :value.sync="$v.form.course.$model"
                    />
                    <b-form-invalid-feedback>
                        <span v-if="!$v.form.course.required">
                            {{ $t('form.validations.required') }}
                        </span>
                        <span v-if="!$v.form.course.maxLength">
                            {{
                                $t('form.validations.maxLength', {
                                    value: '255',
                                })
                            }}
                        </span>
                    </b-form-invalid-feedback>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="4" xl="4" sm="4">
                    <academic-formation
                        :label="
                            `${$t(
                                'miniCurriculum.form.label.academicFormation'
                            )}`
                        "
                        :state="chkState('academicFormation')"
                        :value.sync="$v.form.academicFormation.$model"
                    >
                        <span v-if="!$v.form.academicFormation.required">
                            {{ $t('form.validations.required') }}
                        </span>
                    </academic-formation>
                </b-col>

                <b-col md="4" xl="4" sm="4">
                    <situation
                        :label="`${$t('miniCurriculum.form.label.situation')}`"
                        :state="chkState('situation')"
                        :value.sync="$v.form.situation.$model"
                    >
                        <span v-if="!$v.form.situation.required">
                            {{ $t('form.validations.required') }}
                        </span>
                    </situation>
                </b-col>

                <b-col md="4" xl="4" sm="4">
                    <b-form-group
                        :label="`${$t('miniCurriculum.form.label.conclusion')}`"
                        label-for="input-conclusion-date"
                    >
                        <v-date-picker
                            v-model="form.conclusion"
                            nav-visibility="click"
                            :popover="{ visibility: 'click' }"
                        >
                            <template v-slot="{ inputValue, inputEvents }">
                                <b-form-input
                                    class="mb-2"
                                    :value="inputValue"
                                    v-on="inputEvents"
                                    id="input-conclusion-date"
                                    @change.native="
                                        updateValue($event.target.value)
                                    "
                                    autocomplete="off"
                                    :state="chkState('conclusion')"
                                    readonly
                                />
                                <b-form-invalid-feedback>
                                    <span v-if="!$v.form.conclusion.required">
                                        {{ $t('form.validations.required') }}
                                    </span>
                                </b-form-invalid-feedback>
                            </template>
                        </v-date-picker>
                    </b-form-group>
                </b-col>
            </b-row>

            <ButtonLoader
                class="float-right ml-1"
                type="submit"
                variant="primary"
                :disabled="btnLoader"
                :loader="btnLoader"
            >
                {{ $t('form.button.save') }}
            </ButtonLoader>
            <b-button
                class="float-right"
                variant="secondary"
                :disabled="btnLoader"
                @click="goBack"
            >
                {{ $t('form.button.cancel') }}
            </b-button>
        </b-form>
    </div>
</template>

<script>
import { AcademicFormation, Situation } from '@/views/components/fields'
import Loader from '@/views/components/loader'
import { validationMixin } from 'vuelidate'
import Avatar from 'vue-avatar'
import notification, { danger } from '@/lib/notification'
import { setupCalendar, DatePicker as VDatePicker } from 'v-calendar'
import ButtonLoader from '@/views/components/button-loader'
import { mapperFormationToForm } from '../mappers/mapperFormationToForm'
import { mapperToOption } from '@/views/components/selects'
import { required, maxLength } from 'vuelidate/lib/validators'

notification.init()

setupCalendar({
    popoverDirection: 'bottom',
})

const initialState = {
    id: null,
    course: null,
    situation: null,
    institution: null,
    conclusion: new Date(),
    academicFormation: null,
}

export default {
    name: 'FormFormation',
    components: {
        AcademicFormation,
        Situation,
        Loader,
        Avatar,
        VDatePicker,
        ButtonLoader,
    },
    props: ['formation'],
    mixins: [validationMixin],
    data() {
        return {
            form: Object.assign({}, initialState),
            btnLoader: false,
            loader: true,
        }
    },
    watch: {
        formation(formation) {
            this.form = mapperFormationToForm(formation, mapperToOption)
        },
    },
    computed: {
        isValid() {
            return !this.$v.form.$anyError
        },
        isDirty() {
            return this.$v.form.$anyDirty
        },
    },
    validations: {
        form: {
            course: {
                required,
                maxLength: maxLength(255),
            },
            academicFormation: {
                required,
            },
            situation: {
                required,
            },
            institution: {
                required,
                maxLength: maxLength(255),
            },
            conclusion: {
                required,
            },
        },
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        onSubmit() {
            if (this.validate()) {
                this.$nextTick(() => {
                    this.$emit(
                        'onSubmit',
                        this.form,
                        this.onReset,
                        this.isLoading
                    )
                })
            }
        },
        isLoading(state) {
            this.btnLoader = state
        },
        onReset() {
            this.form = Object.assign({}, initialState)

            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        chkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        validate() {
            this.$v.$touch()

            return this.isValid
        },
    },
    notificationError() {
        danger()
    },
}
</script>

<style scoped>
.btn.disabled {
    cursor: auto;
}
.form-control[readonly] {
    background-color: initial;
}
</style>
